import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback } from "react";
import { backendService } from "../services/backend.service";
import type { CensusPointInTimes } from "../utils/models";

// Not exported: nothing outside this hook builds or invalidates this key — the endpoint is read-only,
// so there is no mutation that would need to evict it.
const CENSUS_POINT_IN_TIMES_QUERY_KEY = "censusPointInTimes";

/**
 * The readings answer a different question every hour — at 07:59 the day has one, at 08:01 it has two —
 * so a long-lived cache would print a stale set on a reprint minutes later. 30s is long enough to
 * collapse the burst of preview regenerations while the user toggles printout settings, short enough
 * that crossing 08:00 or 16:00 is picked up on the next print.
 */
const STALE_TIME_MS = 30_000;

/**
 * Loads a day's CDPH §72329.2 census readings (APP-548), for a caller that prints with or without them.
 *
 * Imperative rather than a `useQuery` subscription for two reasons: the data is only needed when the
 * user prints or previews — a subscription would fetch for every head nurse who merely opens the Day
 * Schedule — and nothing on screen renders it, so subscribing would re-render the whole header on
 * arrival for no visual change. `fetchQuery` still gives us the cache, in-flight deduplication and
 * devtools visibility that a hand-rolled `await` would not.
 *
 * Resolves to `undefined` whenever the block must not be rendered: `enabled` false (a non-California
 * department), a department whose EMR cannot serve readings at an instant, or a failed
 * request. A failure never rejects — losing the compliance numbers must not cost the user their
 * printout — but it is never silent either: `onFailure` is mandatory, and the caller owns how it is
 * reported (analytics, snackbar) since only the caller has that context.
 */
export const useCensusPointInTimesLoader = (departmentId: string, enabled: boolean, onFailure: (error: unknown) => void) => {
	const queryClient = useQueryClient();

	return useCallback(
		async (date: DateTime): Promise<CensusPointInTimes | undefined> => {
			// Cheap local condition before any await: a gated-out department issues no request at all.
			if (!enabled) return undefined;
			try {
				const readings = await queryClient.fetchQuery({
					queryKey: [CENSUS_POINT_IN_TIMES_QUERY_KEY, departmentId, date.toISODate()],
					queryFn: async () => (await backendService.getCensusPointInTimes({ departmentId, date })).data,
					staleTime: STALE_TIME_MS,
				});
				return readings.available ? readings : undefined;
			} catch (error) {
				onFailure(error);
				return undefined;
			}
		},
		[queryClient, departmentId, enabled, onFailure],
	);
};
